import { useEffect, useId, useState } from 'react';
import { IconBeach, IconBriefcase, IconClockPlus, IconEdit, IconPaperclip, IconSend } from '@tabler/icons-react';
import { Button, Chip, Drawer, Input, Segmented, Select, useToast } from '@hana/ui';
import type { RequestType } from '../data';
import { useT } from '@hana/i18n';
import './requestDrawer.i18n';
import { dm, monthAbbr, num } from '../home/view';
import type { useMee } from '../useMee';
import s from '../mee.module.css';
import {
  FIX_KINDS, LEAVE_TYPES, buildRequest, dayOptions, evaluateDraft, newDraft, otWindows, sentLabel,
  type HalfKind, type RequestDraft,
} from './draft';

const KINDS = [
  { key: 'leave', label: 'rd.kLeave', Icon: IconBeach, color: 'var(--d-blue)' },
  { key: 'ot', label: 'rd.kOt', Icon: IconClockPlus, color: 'var(--d-amber)' },
  { key: 'trip', label: 'rd.kTrip', Icon: IconBriefcase, color: 'var(--d-violet)' },
  { key: 'fix', label: 'rd.kFix', Icon: IconEdit, color: 'var(--d-gray)' },
] as const;
const HALVES = [
  { key: 'full', label: 'rd.full' },
  { key: 'AM', label: 'rd.am' },
  { key: 'PM', label: 'rd.pm' },
  { key: 'HR', label: 'rd.hr' },
] as const;

/** Ngăn kéo "Tạo đơn" (Mint-Mee): 4 loại, xem trước số ngày / giờ theo lịch công ty, đường duyệt theo ghế. Gửi đơn giả lập trong bộ nhớ. */
export function RequestDrawer({ open, onOpenChange, m, kind = 'leave', day }: { open: boolean; onOpenChange: (o: boolean) => void; m: ReturnType<typeof useMee>; kind?: RequestType; day?: string }) {
  const t = useT();
  const toast = useToast();
  const rid = useId();
  const me = m.me!;
  const [d, setD] = useState<RequestDraft>(() => ({ ...newDraft(m.cal, kind, day ?? m.today), handover: me.bk?.[0] ?? '' }));
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (open) {
      setD({ ...newDraft(m.cal, kind, day ?? m.today), handover: me.bk?.[0] ?? '' });
      setTried(false);
    }
  }, [open, kind, day, m.today]);

  const ev = evaluateDraft(m.cal, me, d, m.all, m.today);
  const err = tried ? Object.values(ev.errors)[0] : undefined;
  const set = (p: Partial<RequestDraft>) =>
    setD((x) => {
      const n = { ...x, ...p };
      if (p.from && n.kind === 'ot') {
        const ws = otWindows(m.cal, n.from);
        if (!ws.includes(n.window)) n.window = ws[0] ?? '';
      }
      return n;
    });
  const fromOpts =
    d.kind === 'ot' ? dayOptions(m.cal, shiftDay(m.today, -8), 9).reverse()
    : d.kind === 'fix' ? dayOptions(m.cal, shiftDay(m.today, -14), 15).filter((o) => m.cal.isWorkDay(o.value)).reverse().slice(0, 10)
    : dayOptions(m.cal, m.today, 40);
  const toOpts = dayOptions(m.cal, d.from, 40);
  const paper = d.kind === 'leave' && /Ốm|Con ốm|Thai sản/.test(d.ltype);

  const submit = () => {
    setTried(true);
    if (Object.keys(ev.errors).length) return;
    const req = buildRequest(d, ev, me.id, sentLabel(m.today, m.now, monthAbbr(m.today)));
    toast({ msg: t('rd.sent', { id: req.id, seat: me.route[0]![0] }), undo: m.submit(req) });
    onOpenChange(false);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('rd.title')}
      footer={
        <>
          <span className={s.drErr} role={err ? 'alert' : undefined}>{err}</span>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>{t('rd.cancel')}</Button>
          <Button variant="primary" icon={<IconSend size={16} />} onClick={submit}>{t('rd.send')}</Button>
        </>
      }
    >
      <div className={s.drBody}>
        <div className={s.kinds} role="tablist" aria-label={t('rd.type')}>
          {KINDS.map(({ key, label, Icon, color }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={d.kind === key}
              className={s.kindTab}
              onClick={() => { set({ kind: key, from: key === 'leave' || key === 'trip' ? m.today : d.from }); setTried(false); }}
            >
              <Icon size={20} aria-hidden="true" style={{ color }} />
              {t(label)}
            </button>
          ))}
        </div>
        {d.kind === 'leave' && <Select label={t('rd.leaveType')} options={LEAVE_TYPES} value={d.ltype} onChange={(e) => set({ ltype: e.target.value })} />}
        <div className={s.drRow}>
          <Select
            label={d.kind === 'fix' ? t('rd.fixDay') : d.kind === 'ot' ? t('rd.otDay') : t('rd.from')}
            options={fromOpts}
            value={d.from}
            onChange={(e) => set({ from: e.target.value, to: e.target.value > d.to ? e.target.value : d.to })}
          />
          {(d.kind === 'leave' || d.kind === 'trip') && <Select label={t('rd.to')} options={toOpts} value={d.to < d.from ? d.from : d.to} onChange={(e) => set({ to: e.target.value })} />}
          {d.kind === 'ot' && <Select label={t('rd.window')} options={otWindows(m.cal, d.from).map((w) => ({ value: w, label: w.replace('-', ' – ') }))} value={d.window} onChange={(e) => set({ window: e.target.value })} />}
          {d.kind === 'fix' && <Select label={t('rd.content')} options={FIX_KINDS.map(([value, label]) => ({ value, label }))} value={d.fix} onChange={(e) => set({ fix: e.target.value as RequestDraft['fix'] })} />}
        </div>
        {d.kind === 'leave' && <Segmented kind="toggle" label={t('rd.half')} items={HALVES.map((h) => ({ ...h, label: t(h.label) }))} value={d.half} onChange={(h: HalfKind) => set({ half: h })} />}
        {d.kind === 'trip' && <Input label={t('rd.place')} value={d.place} placeholder="Cảng Cái Mép · khách Gỗ Trường Thành Phát" onChange={(e) => set({ place: e.target.value })} />}
        <div className={s.field}>
          <label htmlFor={`${rid}-r`} className={s.fieldLb}>{d.kind === 'fix' ? t('rd.explain') : t('rd.reason')} <span className={s.req}>*</span></label>
          <textarea
            id={`${rid}-r`}
            className={s.textarea}
            data-bad={tried && ev.errors.reason ? 1 : undefined}
            aria-invalid={tried && ev.errors.reason ? true : undefined}
                        value={d.reason}
            placeholder={d.kind === 'ot' ? t('rd.phOt') : d.kind === 'fix' ? t('rd.phFix') : t('rd.phLeave')}
            onChange={(e) => set({ reason: e.target.value })}
          />
        </div>
        {d.kind === 'leave' && !!me.bk?.length && (
          <Select label={t('rd.handover')} options={me.bk.map((v) => ({ value: v, label: v }))} value={d.handover} onChange={(e) => set({ handover: e.target.value })} />
        )}
        {d.kind === 'leave' && (
          <button type="button" className={s.attach} aria-pressed={d.paper} onClick={() => set({ paper: !d.paper })}>
            <IconPaperclip size={16} aria-hidden="true" />
            {d.paper ? t('rd.attached') : paper ? t('rd.attachReq') : t('rd.attachOpt')}
          </button>
        )}
        <div className={s.calc} aria-label={t('rd.preview')}>
          {d.kind === 'ot' ? (
            <><div><span>{t('rd.hours')}</span><b>{num(ev.ot)} {t('rd.hoursU')}</b></div><div title={t('rd.byCal')}><span>{t('rd.dayType')}</span><b>{m.cal.isHoliday(d.from) ? t('rd.holiday') : m.cal.isWorkDay(d.from) ? t('rd.workday') : t('rd.weekend')}</b></div><div><span>{t('rd.code')}</span><b>{ev.code}</b></div></>
          ) : d.kind === 'fix' ? (
            <><div><span>{t('rd.date')}</span><b>{dm(d.from)}</b></div><div title={t('rd.byCal2')}><span>{t('rd.due')}</span><b>{dm(ev.fixDue)}</b></div><div><span>{t('rd.code')}</span><b>{ev.code}</b></div></>
          ) : (
            <>
              <div title={t('rd.byCal') + (ev.offDays ? t('rd.skipped', { n: ev.offDays }) : '')}><span>{t('rd.days')}</span><b>{num(ev.days)}</b></div>
              <div><span>{d.kind === 'leave' ? t('rd.leftAfter') : t('rd.code')}</span><b data-bad={ev.annual && ev.leftAfter < 0 ? 1 : undefined}>{d.kind === 'leave' ? (ev.annual ? num(ev.leftAfter) : t('rd.noDeduct')) : ev.code}</b></div>
              <div title={t('rd.skipTitle')}><span>{t('rd.skip')}</span><b>{ev.offDays}</b></div>
            </>
          )}
        </div>
        <div className={s.route}>
          <span>{t('rd.route')}</span>
          {me.route.map(([seat, who, tip], i) => (
            <span key={seat} title={tip}><Chip>{i + 1}. {seat} · {who}</Chip></span>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

function shiftDay(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}
